import {
  MediaStreamKind,
  MediaStreamTrackInfo,
  RoomContextType,
  RoomProviderProps,
  User,
  UserData,
} from '@/types'
import { createContext, useContext, useEffect, useReducer } from 'react'
import useAuthUser from 'react-auth-kit/hooks/useAuthUser'
import { usePeerConnection, useWebSocket } from './connections'

const RoomContext = createContext<RoomContextType | null>(null)

const initialRoomState: RoomContextType = {
  roomId: 0,
  roomName: '',
  users: new Map(),
  tracks: new Map(),
  mediaStreams: new Map(),
  toggleMediaState: () => {},
}

function roomReducer(state: RoomContextType, action: any): RoomContextType {
  switch (action.type) {
    case 'room-updated': {
      const updatedUsers = new Map(state.users)
      const updatedTracks = new Map(state.tracks)
      const updatedMediaStreams = new Map(state.mediaStreams)
      const serverUsers = action.payload.users as User[]

      serverUsers.forEach((serverUser: User) => {
        const existingUser = updatedUsers.get(serverUser.id)
        if (existingUser) {
          // Check if tracks need to be replaced
          const existingStreamIds = new Set(
            existingUser.tracks?.map((track) => track.streamId)
          )
          const incomingStreamIds = new Set(
            serverUser.tracks?.map((track) => track.streamId)
          )

          let tracksToUpdate = existingUser.tracks || []
          if (
            serverUser.tracks &&
            (existingStreamIds.size !== incomingStreamIds.size ||
              ![...existingStreamIds].every((id) => incomingStreamIds.has(id)))
          ) {
            // Replace tracks if the streamIds are different
            tracksToUpdate = serverUser.tracks
          }

          // Merge updates for existing users
          updatedUsers.set(serverUser.id, {
            ...existingUser,
            ...serverUser,
            tracks: tracksToUpdate,
          })
        } else {
          // Add new users
          updatedUsers.set(serverUser.id, serverUser)
        }

        serverUser.tracks?.forEach((track) => {
          updatedTracks.set(track.id, serverUser.id)
          updatedMediaStreams.set(track.streamId!, serverUser.id)
        })
      })

      // Remove users from the frontend state that no longer exist on the server
      state.users.forEach((_, userId) => {
        const hasUser = serverUsers.some((u) => u.id === userId)
        if (!hasUser) {
          console.log(`Removing user ${userId} from frontend state`)

          // Clean up tracks and media streams associated with this user
          state.tracks.forEach((ownerId, trackId) => {
            if (ownerId === userId) {
              updatedTracks.delete(trackId)
            }
          })

          state.mediaStreams.forEach((ownerId, streamId) => {
            if (ownerId === userId) {
              updatedMediaStreams.delete(streamId)
            }
          })

          // Finally, remove the user
          updatedUsers.delete(userId)
        }
      })

      return {
        ...state,
        roomId: action.payload.roomId || state.roomId,
        roomName: action.payload.roomName || state.roomName,
        users: updatedUsers,
        tracks: updatedTracks,
        mediaStreams: updatedMediaStreams,
      }
    }

    case 'toggle-media-state': {
      const { userId, name, kind, enabled, stream } = action.payload

      const updatedUsers = new Map(state.users)
      const updatedTracks = new Map(state.tracks)
      const updatedMediaStreams = new Map(state.mediaStreams)

      const user = updatedUsers.get(userId)

      if (user) {
        const updatedTracksList = user.tracks || []
        const trackIndex = updatedTracksList.findIndex((t) => t.type === kind)

        let newTracks
        if (enabled) {
          // Add or update the track
          const track = stream?.getTracks()[0]
          const newTrackInfo: MediaStreamTrackInfo = {
            id: track?.id || '',
            type: kind,
            streamId: stream.id,
            track,
          }

          if (trackIndex !== -1) {
            newTracks = [
              ...updatedTracksList.slice(0, trackIndex),
              newTrackInfo,
              ...updatedTracksList.slice(trackIndex + 1),
            ]
          } else {
            newTracks = [...updatedTracksList, newTrackInfo]
          }

          // Update maps
          updatedTracks.set(newTrackInfo.id, userId)
          updatedMediaStreams.set(newTrackInfo.streamId!, userId)
        } else {
          // Remove the track
          newTracks = updatedTracksList.filter((t) => t.type !== kind)
        }

        updatedUsers.set(userId, {
          ...user,
          [kind === 'audio'
            ? 'isMicEnabled'
            : kind === 'video'
            ? 'isVideoEnabled'
            : 'isScreenEnabled']: enabled,
          tracks: newTracks,
        })
      } else {
        const track = stream?.getTracks()[0]
        const newTrackInfo: MediaStreamTrackInfo = {
          id: track?.id || '',
          type: kind,
          streamId: stream.id,
          track,
        }

        updatedUsers.set(userId, {
          id: userId,
          name,
          [kind === 'audio'
            ? 'isMicEnabled'
            : kind === 'video'
            ? 'isVideoEnabled'
            : 'isScreenEnabled']: enabled,
          tracks: [newTrackInfo],
        })

        // Update maps
        updatedTracks.set(newTrackInfo.id, userId)
        updatedMediaStreams.set(newTrackInfo.streamId!, userId)
      }

      return {
        ...state,
        users: updatedUsers,
        tracks: updatedTracks,
        mediaStreams: updatedMediaStreams,
      }
    }

    case 'track-metadata': {
      const { trackId, streamId, trackType, senderId } = action.payload
      console.log(
        '📹 Reducer: Processing track-metadata for user:',
        senderId,
        'track type:',
        trackType
      )
      console.log('🔍 Looking for user with ID:', String(senderId))
      console.log('🔍 Available user IDs:', Array.from(state.users.keys()))

      const updatedUsers = new Map(state.users)
      const updatedTracks = new Map(state.tracks)
      const updatedMediaStreams = new Map(state.mediaStreams)

      // Find the user by senderId (convert to string for comparison)
      const user = Array.from(state.users.values()).find(
        (u) => u.id === String(senderId)
      )

      if (user) {
        console.log(
          '✅ Found user for track metadata:',
          user.name,
          'id:',
          user.id
        )

        // Check if this track already exists
        const existingTrack = user.tracks?.find((t) => t.id === trackId)
        if (existingTrack) {
          console.log('⚠️ Track already exists, skipping duplicate:', trackId)
          return state
        }

        // Create a placeholder track info for this metadata
        const trackInfo: MediaStreamTrackInfo = {
          id: trackId,
          streamId: streamId,
          type: trackType,
          track: undefined, // Will be set when WebRTC ontrack event fires
        }

        // Add the track to the user's tracks
        const updatedTracksList = [...(user.tracks || []), trackInfo]
        console.log('📹 User now has', updatedTracksList.length, 'tracks')

        updatedUsers.set(user.id, {
          ...user,
          tracks: updatedTracksList,
        })

        // Update maps
        updatedTracks.set(trackId, user.id)
        updatedMediaStreams.set(streamId, user.id)
        console.log('✅ Track metadata processed successfully')
      } else {
        console.warn(`❌ No user found for senderId: ${senderId}`)
        console.log('Available users:', Array.from(state.users.keys()))
      }

      return {
        ...state,
        users: updatedUsers,
        tracks: updatedTracks,
        mediaStreams: updatedMediaStreams,
      }
    }

    case 'new-track': {
      const { tracks } = action.payload
      console.log(
        '🎥 Reducer: Processing new-track with',
        tracks.length,
        'tracks'
      )

      const updatedUsers = new Map(state.users)
      const updatedTracks = new Map(state.tracks)
      const updatedMediaStreams = new Map(state.mediaStreams)

      tracks.forEach((newTrack: MediaStreamTrackInfo) => {
        console.log('🎥 Processing track:', {
          id: newTrack.id,
          streamId: newTrack.streamId,
          type: newTrack.type,
          hasTrack: !!newTrack.track,
        })

        const userId = updatedMediaStreams.get(newTrack.streamId!)

        if (userId) {
          console.log('✅ Found user for track:', userId)
          const user = updatedUsers.get(userId)

          if (user) {
            console.log('✅ Updating user tracks for:', user.name)
            const updatedTracksList =
              user.tracks?.map((existingTrack) => {
                // Replace the track if it matches by ID
                if (existingTrack.streamId === newTrack.streamId) {
                  console.log('🔄 Replacing existing track with actual track')
                  return { ...existingTrack, track: newTrack.track }
                }
                return existingTrack
              }) || []

            updatedUsers.set(userId, {
              ...user,
              tracks: updatedTracksList,
            })

            // Update maps
            updatedTracks.set(newTrack.id, userId)
            updatedMediaStreams.set(newTrack.streamId!, userId)
            console.log('✅ Track associated with user successfully')
          } else {
            console.warn('❌ User not found in updatedUsers map')
          }
        } else {
          console.warn(`❌ No user found for streamId: ${newTrack.streamId}`)
          console.log(
            'Available streamIds in mediaStreams:',
            Array.from(updatedMediaStreams.keys())
          )
        }
      })

      return {
        ...state,
        users: updatedUsers,
        tracks: updatedTracks,
        mediaStreams: updatedMediaStreams,
      }
    }

    case 'remove-track': {
      const { trackId } = action.payload // The ID of the track to remove

      const updatedUsers = new Map(state.users)
      const updatedTracks = new Map(state.tracks)
      const updatedMediaStreams = new Map(state.mediaStreams)

      // Find the user associated with this track
      const userId = state.tracks.get(trackId)

      if (userId) {
        const user = updatedUsers.get(userId)

        if (user) {
          // Remove the track from the user's track list
          const updatedUserTracks =
            user.tracks?.filter((track) => track.id !== trackId) || []

          // Update the user object
          updatedUsers.set(userId, {
            ...user,
            tracks: updatedUserTracks,
          })

          // If the track had a unique `streamId!`, check if it should also be removed from `mediaStreams`
          const removedTrack = user.tracks?.find(
            (track) => track.id === trackId
          )
          if (removedTrack) {
            const streamId = removedTrack.streamId!

            // Remove the stream ID if no other tracks use it
            const isStreamStillUsed = Array.from(updatedUsers.values()).some(
              (u) => u.tracks?.some((t) => t.streamId === streamId)
            )

            if (!isStreamStillUsed) {
              updatedMediaStreams.delete(streamId)
            }
          }
        }

        // Remove the track from the `tracks` map
        updatedTracks.delete(trackId)
      } else {
        console.warn(`Track with ID ${trackId} not found in tracks map.`)
      }

      return {
        ...state,
        users: updatedUsers,
        tracks: updatedTracks,
        mediaStreams: updatedMediaStreams,
      }
    }

    case 'webrtc-track': {
      const { track, stream, participantId } = action.payload
      console.log('🎥 Reducer: Processing WebRTC track:', {
        trackId: track.id,
        kind: track.kind,
        streamId: stream.id,
        participantId,
      })

      const updatedUsers = new Map(state.users)
      const updatedTracks = new Map(state.tracks)
      const updatedMediaStreams = new Map(state.mediaStreams)

      // Find user by participant ID (assuming participantId maps to user ID)
      const user = Array.from(state.users.values()).find(
        (u) => u.id === participantId
      )

      if (user) {
        console.log(
          '✅ Found user for WebRTC track:',
          user.name,
          'id:',
          user.id
        )

        // Create track info
        const trackInfo: MediaStreamTrackInfo = {
          id: track.id,
          streamId: stream.id,
          type: track.kind as MediaStreamKind,
          track: track,
        }

        // Check if this track already exists
        const existingTrack = user.tracks?.find((t) => t.id === track.id)
        if (existingTrack) {
          console.log('⚠️ Track already exists, updating:', track.id)
          // Update existing track
          const updatedTracksList =
            user.tracks?.map((t) => (t.id === track.id ? trackInfo : t)) || []
          updatedUsers.set(user.id, {
            ...user,
            tracks: updatedTracksList,
          })
        } else {
          // Add new track
          const updatedTracksList = [...(user.tracks || []), trackInfo]
          updatedUsers.set(user.id, {
            ...user,
            tracks: updatedTracksList,
          })
        }

        // Update maps
        updatedTracks.set(track.id, user.id)
        updatedMediaStreams.set(stream.id, user.id)
        console.log('✅ WebRTC track processed successfully')
      } else {
        console.warn(`❌ No user found for participantId: ${participantId}`)
      }

      return {
        ...state,
        users: updatedUsers,
        tracks: updatedTracks,
        mediaStreams: updatedMediaStreams,
      }
    }

    default:
      return state
  }
}

export const useRoom = () => useContext(RoomContext)

export const RoomProvider: React.FC<RoomProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(roomReducer, initialRoomState)
  const socketContext = useWebSocket()
  const peerConnection = usePeerConnection()
  const userData = useAuthUser<UserData>()

  const toggleMediaState = async (
    kind: MediaStreamKind,
    enabled: boolean,
    stream?: MediaStream
  ) => {
    dispatch({
      type: 'toggle-media-state',
      payload: {
        userId: userData?.user_id + '',
        name: userData?.email,
        kind,
        enabled,
        stream,
      },
    })

    // Track metadata is handled via WebSocket messages in the ControlBar component
    // No need for HTTP API calls here
  }

  useEffect(() => {
    const handleRoomPayload = (payload: any) => {
      switch (payload.type) {
        case 'room-updated':
          dispatch({
            type: 'room-updated',
            payload: payload.payload,
          })
          break
      }
    }

    const handleTrackMetadata = (event: CustomEvent) => {
      const message = event.detail
      console.log('📹 Processing track-metadata:', {
        trackId: message.track_id,
        streamId: message.stream_id,
        trackType: message.track_type,
        senderId: message.sender_id,
      })

      // Dispatch a new action to handle track metadata
      dispatch({
        type: 'track-metadata',
        payload: {
          trackId: message.track_id,
          streamId: message.stream_id,
          trackType: message.track_type,
          senderId: message.sender_id,
        },
      })
      console.log('✅ Track metadata dispatched to reducer')
    }

    const handleWebRTCTrack = (event: CustomEvent) => {
      const { track, stream, participantId } = event.detail
      console.log('🎥 Processing WebRTC track event:', {
        trackId: track.id,
        kind: track.kind,
        streamId: stream.id,
        participantId,
      })

      dispatch({
        type: 'webrtc-track',
        payload: {
          track,
          stream,
          participantId,
        },
      })
      console.log('✅ WebRTC track dispatched to reducer')
    }

    const eventListener = (event: MessageEvent) => {
      try {
        const payload = JSON.parse(event.data)
        handleRoomPayload(payload)
      } catch (err) {
        console.error('Error parsing WebSocket message:', err)
      }
    }

    socketContext?.socket.addEventListener('message', eventListener)
    window.addEventListener(
      'track-metadata',
      handleTrackMetadata as EventListener
    )
    window.addEventListener('webrtc-track', handleWebRTCTrack as EventListener)

    // Handle track removal and ended events
    if (peerConnection?.connection) {
      peerConnection.connection.ontrack = (event) => {
        const track = event.track
        console.log('🎥 Track received via ontrack:', {
          id: track.id,
          kind: track.kind,
          label: track.label,
          enabled: track.enabled,
          readyState: track.readyState,
        })

        if (event.streams && event.streams[0]) {
          const stream = event.streams[0]
          console.log(
            `🎥 Received stream: ${stream.id} with ${
              stream.getTracks().length
            } tracks`
          )

          stream.onremovetrack = (removeEvent) => {
            console.log('🗑️ Track removed:', removeEvent.track.id)
            dispatch({
              type: 'remove-track',
              payload: {
                trackId: removeEvent.track.id,
              },
            })
          }

          track.onended = () => {
            console.log('🔚 Track ended:', track.id)
            dispatch({
              type: 'remove-track',
              payload: {
                trackId: track.id,
              },
            })
          }

          // Dispatch the track via custom event (handled by handleWebRTCTrack)
          window.dispatchEvent(
            new CustomEvent('webrtc-track', {
              detail: {
                track,
                stream,
                participantId: userData?.user_id?.toString() || 'unknown',
              },
            })
          )
        }
      }
    }

    return () => {
      socketContext?.socket.removeEventListener('message', eventListener)
      window.removeEventListener(
        'track-metadata',
        handleTrackMetadata as EventListener
      )
      window.removeEventListener(
        'webrtc-track',
        handleWebRTCTrack as EventListener
      )
    }
  }, [socketContext?.socket, state.roomId])

  return (
    <RoomContext.Provider value={{ ...state, toggleMediaState }}>
      {children}
    </RoomContext.Provider>
  )
}
